import { useEffect, useState } from 'react';
import {
  FiLink, FiExternalLink,
} from 'react-icons/fi';
import { useSelector } from 'react-redux';
import EventMenuOption from '../components/EventMenuOption';
import authenticate from '../utils/authenticate';
import { UserRole } from '../models/user';
import useNavigateWithQuery from '../hooks/useNavigateWithQuery';
import useGetUserRole from '../hooks/useGetUserRole';
import useGetSelectedEvent from '../hooks/useGetSelectedEvent';
import LoadingLottie from '../components/atoms/LoadingLottie';
import copyLinkToClipboard from '../utils/copyLinkToClipboard';
import config from '../config/config';
import AdminSectionTitle from '../components/AdminSectionTitle';
import {
  RootState, useGetEventCapacityQuery, useGetOneSellerSummaryQuery,
} from '../store';
import getToastColor from '../utils/getToastColor';
import getToastText from '../utils/getToastText';
import { copyReferralLink, useDownloadQR } from '../utils/sellerUtils';
import useGetSelectedBusiness from '../hooks/useGetSelectedBusiness';
import CloneEvent from '../components/CloneEvent';
import TopSellersRanking from '../components/TopSellersRanking';
import CoinsHand from '../components/icons/CoinsHand';
import QrCode02Icon from '../components/icons/QrCode02Icon';
import {
  IconAvantti1, IconCard1, IconCard2, IconCard3, IconEnjoy1, IconMove1, IconMove2, IconMove3, IconPlay1, IconPlay2, IconSmile1, IconSmile2, IconTicket1, IconTicket3,
} from '../components/icons';
import isAllowed from '../utils/isAllowed';

interface Options {
  title: string;
  info: string;
  buttonLabel: string;
  redirectPath?: string;
  onClick?: () => void;
  allowedUsers: UserRole[];
  hidden?: boolean;
  image?: string;
  icon?: React.ReactNode;
}

const EventMenu: React.FC = () => {
  const navigate = useNavigateWithQuery();
  const { event, eventLoading } = useGetSelectedEvent();
  const role = useGetUserRole();
  const userData = useSelector((state: RootState) => state.auth.user);
  const { business: loadedBusiness, businessLoading } = useGetSelectedBusiness();
  const handleDownloadQR = useDownloadQR();
  const { data: capacity } = useGetEventCapacityQuery({ eventId: event?.id ?? '', businessId: event?.business_id ?? '' }, { skip: !event });
  const { data: sellerSummary, isLoading: isSellerSummaryLoading } = useGetOneSellerSummaryQuery({
    businessId: event?.business_id ?? '',
    eventId: event?.id ?? '',
    sellerId: userData?.id ?? '',
  }, { skip: role !== 'SELLER' });
  const [isOpen, setIsOpen] = useState(false);
  useEffect(() => {
    if (role === 'SCANNER') {
      navigate('scanner', { replace: true });
    } else if (role === 'CASHIER') {
      navigate('inperson', { replace: true });
    }
  }, [role, navigate, userData?.id]);

  if (businessLoading) {
    return <LoadingLottie />;
  }

  if (!loadedBusiness) {
    return <div>No business found</div>;
  }

  const options: Options[] = [
    ...((sellerSummary?.user?.current_event?.active && isAllowed(userData, UserRole.SELLER, event?.business_id ?? '')) ? [{
      title: 'Generar entradas',
      info: 'En este apartado podrás generar entradas de forma manual, enviandolas por correo o por otros medios',
      buttonLabel: 'Generar entradas',
      redirectPath: event?.has_simplified_selling ? 'orders/simplified-manual' : 'orders/new/tickets',
      allowedUsers: [UserRole.SELLER],
      icon: <IconEnjoy1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Copiar link de venta',
      info: 'Podrás copiar tu link de venta, el cual contabilizará todas las ventas que se generen desde dicho link.',
      buttonLabel: 'Copiar link',
      onClick: () => {
        if (!event || !sellerSummary) {
          return;
        }
        copyReferralLink(loadedBusiness, event, sellerSummary);
      },
      allowedUsers: [UserRole.SELLER],
      icon: <IconSmile1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Descargar QR de pública',
      info: 'Podrás descargar el QR correspondiente a tu perfil de pública para que se contabilicen las personas que ingresaron con tu código.',
      buttonLabel: 'Descargar QR',
      onClick: () => {
        if (!sellerSummary || !event) {
          return;
        }
        handleDownloadQR(sellerSummary, event.business_id, event.id);
      },
      allowedUsers: [UserRole.SELLER],
      icon: <IconAvantti1 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !event?.has_seller_qr,
    },
    {
      title: 'Reportes de pública',
      info: 'En este apartado podrás visualizar los diferentes reportes sobre tus ventas, asistentes, movimientos de dinero, etc.',
      buttonLabel: 'Ver reportes',
      redirectPath: 'details',
      allowedUsers: [UserRole.SELLER],
      icon: <IconTicket1 className='w-10 h-10 ml-2 mt-1' />,
    }] : []),
    {
      title: 'Asistentes',
      info: 'En este apartado encontrarás la acción de aceptar solicitudes de entradas, y podrás visualizar los asistentes al evento.',
      buttonLabel: 'Ver asistentes',
      redirectPath: 'orders',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconEnjoy1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Entradas',
      info: 'Aquí podras crear los diferentes tipos de entradas de tu evento, configurar su precio y cantidad de cada una.',
      buttonLabel: 'Editar entradas',
      redirectPath: 'tickets',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconTicket1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Reportes',
      info: 'En este apartado podrás descargar reportes de asistencia, pagos y respuestas de encuestas.',
      buttonLabel: 'Ver reportes',
      redirectPath: 'sales-report',
      allowedUsers: [UserRole.ADMIN, UserRole.COPRODUCER],
      hidden: !event?.coproducer_reports && role === UserRole.COPRODUCER,
      icon: <IconAvantti1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Cupones',
      info: 'Aquí podras crear y modificar los cupones de descuento de tu evento.',
      buttonLabel: 'Editar cupones',
      redirectPath: 'coupons',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconCard1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Referidos',
      info: 'Aquí ver y configurar los referidos del evento.',
      buttonLabel: 'Ver referidos',
      redirectPath: 'referrals',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconMove1 className='w-10 h-10 ml-2 mt-1 object-contain' />,
    },
    {
      title: 'Encuestas',
      info: 'Aquí podras ver las respuestas a las preguntas de tu evento.',
      buttonLabel: 'Ver respuestas',
      redirectPath: 'polls',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      hidden: !event?.has_polls,
      icon: <IconPlay1 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Vendedores',
      info: 'Aquí podras configurar los vendedores de este evento y ver sus ventas',
      buttonLabel: 'Ver vendedores',
      redirectPath: 'sellers',
      allowedUsers: [UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconSmile2 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Venta presencial',
      info: 'En este apartado se podrán gestionar las diferentes cajas existentes, crear nuevas, realizar movimientos de efectivo, etc.',
      buttonLabel: 'Ver puntos de venta',
      redirectPath: 'inperson',
      allowedUsers: [UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconMove3 className='w-10 h-10 ml-2 mt-1 object-cover' />,
      hidden: !event?.allow_in_person_sales,
    },
    {
      title: 'Escanear codigos',
      info: 'En este apartado podrás escanear los códigos de los asistentes para que se contabilicen las entradas.',
      buttonLabel: 'Ver',
      redirectPath: 'scanning',
      allowedUsers: [UserRole.ADMIN, UserRole.COPRODUCER, UserRole.EDITOR],
      icon: <IconTicket3 className='w-10 h-10 ml-2 mt-1' />,
    },
    {
      title: 'Liquidaciones',
      info: 'En esta sección podrás visualizar el total de dinero a liquidar y solicitar adelantos de dinero en caso de que tengas esa posibilidad.',
      buttonLabel: 'Ver liquidaciones',
      redirectPath: 'balance',
      allowedUsers: [UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconCard3 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !event?.coproducer_balance && role === UserRole.COPRODUCER,
    },
    {
      title: 'Descuentos',
      info: 'En esta sección podrás crear y modificar los descuentos de tu evento.',
      buttonLabel: 'Ver descuentos',
      redirectPath: 'discounts',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconCard2 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !event?.has_audience_discounts,
    },
    {
      title: 'Compradores promotores',
      info: 'En este apartado podrás dar de alta y configurar la funcionalidad de promotores y seguir sus ventas en tiempo real.',
      buttonLabel: 'Ver',
      redirectPath: 'order-referrals',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      icon: <IconMove2 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !event?.has_order_referrals,
    },
    {
      title: 'Listas',
      info: 'En este apartado podrás crear y gestionar las listas de invitados de tu evento.',
      buttonLabel: 'Ver listas',
      allowedUsers: [UserRole.EDITOR, UserRole.ADMIN, UserRole.COPRODUCER],
      redirectPath: `list/${event?.list?.id}`,
      icon: <IconPlay2 className='w-10 h-10 ml-2 mt-1' />,
      hidden: !event?.has_lists,
    },
  ];

  if (!event || eventLoading) {
    return <LoadingLottie />;
  }

  const handleManualSellClick = () => {
    if (event.has_simplified_selling) {
      navigate(`/admin/${event.business_id}/events/${event.id}/orders/simplified-manual`);
    } else {
      navigate(`/admin/${event.business_id}/events/${event.id}/orders/new/manual`);
    }
  };

  const handleScannerClick = () => {
    navigate('scanner');
  };

  const handleEditEventClick = () => {
    navigate('edit');
  };

  if (isSellerSummaryLoading) {
    return <LoadingLottie />;
  }

  return (
    <div className="flex flex-col gap-8 my-6">
      <div className="grid grid-cols-1 justify-between gap-4">
        <AdminSectionTitle
          className="col-span-1 md:col-span-4"
          title="Eventos"
          subtitle={`${event?.name}`}

          mainButtons={[
            ...(role === 'ROOT' || role === 'ADMIN' || role === 'COPRODUCER' ? [{
              label: 'Generar entradas',
              onClick: () => handleManualSellClick(),
              variant: 'secondary' as const,
              icon: <CoinsHand className='text-xl' />,
            }] : []),
            ...(role === 'ROOT' || role === 'ADMIN' || role === 'COPRODUCER' || role === 'SCANNER' ? [{
              label: 'Escáner QR',
              onClick: () => handleScannerClick(),
              variant: 'secondary' as const,
              icon: <QrCode02Icon className='text-xl' />,
            }] : []),
          ]}

          sideButtons={[
            ...(role !== 'SELLER' ? [{
              icon: <FiLink className='text-xl' />,
              onClick: () => copyLinkToClipboard(`${config.baseDomain}/events/${event.slug}`),
              variant: 'tertiary' as const,
            }] : []),
          ]}

          dropdownItems={[
            ...(role === 'ROOT' || role === 'ADMIN' || role === 'COPRODUCER' || role === 'EDITOR' ? [{
              label: 'Editar evento',
              onClick: () => handleEditEventClick(),
            }] : []),
            ...(role !== 'SELLER' ? [{
              label: 'Copiar link de venta',
              onClick: () => copyLinkToClipboard(`${config.baseDomain}/events/${event.slug}`),
            }] : []),
            ...(role === 'ROOT' || role === 'ADMIN' || role === 'COPRODUCER' || role === 'EDITOR' ? [{
              label: 'Clonar evento',
              onClick: () => setIsOpen(true),
            }] : []),
            ...(role === 'ROOT' ? [{
              label: 'Tipos de escáner',
              onClick: () => navigate('scanner-types'),
            }] : []),
          ]}
        />
        {role !== 'SELLER' && (
          <div className="flex flex-row gap-2 whitespace-nowrap overflow-x-auto scrollbar-hide">
            <p className={`${getToastColor(event.public_status)} px-[10px] py-[2px] rounded-3xl w-fit text-sm-medium text-center items-center`}>{getToastText(event.public_status)}</p>
            <p className={`${event.is_public ? 'bg-primary-500 text-white' : 'bg-warning-50 text-text-primary'} px-[10px] py-[2px] rounded-3xl w-fit text-sm-medium`}>{event.is_public ? 'Visible' : 'No visible'}</p>
            {capacity && <p className={'flex gap-2 items-center bg-primary-500 text-white p-1 rounded-3xl w-fit text-sm-medium cursor-pointer px-[10px] py-[2px]'} onClick={() => navigate(`/admin/${event.business_id}/events/${event.id}/sales-report?scanned-qrs=true`)}><span>{capacity?.used_tickets} QRs escaneados</span> <FiExternalLink className="text-md" /></p>}
          </div>
        )}
      </div>

      <div className='glass-surface'>
        {(role === 'SELLER' && !sellerSummary?.user?.current_event?.active && !isSellerSummaryLoading) && (
          <div className='text-center text-2xl font-bold text-red-500'>Vendedor inactivo</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {options
            .filter((option) => !option.hidden && authenticate(option.allowedUsers, role))
            .map((option) => (
              <div key={option.title}>
                <EventMenuOption
                  id={event?.id ?? 0}
                  title={option.title}
                  info={option.info}
                  buttonLabel={option.buttonLabel}
                  redirectPath={option.redirectPath}
                  onClick={option.onClick}
                  image={option.image}
                  icon={option.icon}
                />
              </div>
            ))}
        </div>
      </div>

      {event?.show_sellers_ranking && role === 'SELLER' && (
        <TopSellersRanking businessId={event.business_id} eventId={event.id} />
      )}

      <CloneEvent event={event} isOpen={isOpen} setIsOpen={setIsOpen} />
    </div>
  );
};

export default EventMenu;
